import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TaskProgressModel } from '../../../api/models/task-progress-model';
import { EventsService } from '../../../events/events.service';
import { NgIf, NgTemplateOutlet, DecimalPipe } from '@angular/common';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-long-task-button',
    template: `
    <!-- Stroked Button -->
    <button *ngIf="buttonType === 'stroked'"
            mat-stroked-button
            type="button"
            matTooltip="{{ isTaskInProgress ? '' : tooltip }}"
            (click)="handleClick($event)">
      <ng-container *ngTemplateOutlet="buttonContent"></ng-container>
    </button>

    <!-- Raised Button -->
    <button *ngIf="buttonType === 'raised'"
            mat-raised-button
            type="button"
            matTooltip="{{ isTaskInProgress ? '' : tooltip }}"
            (click)="handleClick($event)">
      <ng-container *ngTemplateOutlet="buttonContent"></ng-container>
    </button>

    <!-- Flat Button -->
    <button *ngIf="buttonType === 'flat'"
            mat-flat-button
            type="button"
            matTooltip="{{ isTaskInProgress ? '' : tooltip }}"
            (click)="handleClick($event)">
      <ng-container *ngTemplateOutlet="buttonContent"></ng-container>
    </button>

    <!-- Mini Fab Button -->
    <button *ngIf="buttonType === 'mini-fab'"
            mat-mini-fab
            type="button"
            matTooltip="{{ isTaskInProgress ? '' : tooltip }}"
            (click)="handleClick($event)">
      <ng-container *ngTemplateOutlet="buttonContentNoText"></ng-container>
    </button>

    <!-- Template for button content with text -->
    <ng-template #buttonContent>
      <div style="display: flex; align-items: center;">
        <mat-icon *ngIf="!hideIcon && !isTaskInProgress">sync</mat-icon>
        <div class="progress-holder" *ngIf="isTaskInProgress" matTooltip="Task in progress {{ progressValue | number: '1.0-0' }}%">
          <mat-progress-spinner [diameter]="20" [strokeWidth]="1" mode="indeterminate"></mat-progress-spinner>
          <mat-progress-spinner class="total-progress" [diameter]="18" [strokeWidth]="2" [value]="progressValue" mode="determinate"></mat-progress-spinner>
        </div>
        {{ buttonTitle }}
      </div>
    </ng-template>

    <!-- Template for FAB buttons (without text) -->
    <ng-template #buttonContentNoText>
      <mat-icon style="vertical-align: middle;" *ngIf="!hideIcon && !isTaskInProgress">sync</mat-icon>
      <div class="progress-holder" *ngIf="isTaskInProgress" matTooltip="Task in progress {{ progressValue | number: '1.0-0' }}%" [style.marginRight]="buttonTitle ? '5px' : '0px'">
        <mat-progress-spinner [color]="loadingColor" [diameter]="24" [strokeWidth]="1" mode="indeterminate"></mat-progress-spinner>
        <mat-progress-spinner [color]="loadingColor" class="total-progress" [diameter]="22" [strokeWidth]="2" [value]="progressValue" mode="determinate"></mat-progress-spinner>
      </div>
    </ng-template>
  `,
    styles: [
        '.progress-holder {display: inline-block; position: relative; vertical-align: middle; height: 24px; width: 24px;}',
        'mat-progress-spinner { position: absolute; top: 0; left: 0; }',
        'mat-progress-spinner.total-progress { position: absolute; top: 1px; left: 1px; }',
    ],
    standalone: true,
    imports: [
        NgIf,
        MatButton,
        MatTooltip,
        NgTemplateOutlet,
        MatMiniFabButton,
        MatIcon,
        MatProgressSpinner,
        DecimalPipe,
    ],
})
export class LongTaskButtonComponent implements OnDestroy, OnInit {
  @Input() tooltip: string = '';
  @Input() buttonTitle: string = 'Sync Now';
  @Input() taskKey: string;
  @Input() loadingColor: 'primary' | 'accent' | 'warn' = 'primary';
  @Input() clickFunction: () => Promise<void>;
  @Input() buttonType: 'stroked' | 'raised' | 'flat' | 'mini-fab' | 'fab'  = 'stroked';
  @Input() hideIcon: boolean = false;

  taskModel: TaskProgressModel;
  isTaskInProgress = false;
  progressValue: number = 0;

  constructor(private eventService: EventsService) {}

  handleClick(event: Event) {
    console.log('click', event);
    this.handleClickAsync(event).then();
  }

  async handleClickAsync(event: Event) {
    console.log('click', event);
    if (this.taskModel?.status !== 'in-progress' && this.clickFunction) {
      try {
        this.isTaskInProgress = true;
        await this.clickFunction();
        this.isTaskInProgress = false;
      } catch (e) {
        this.isTaskInProgress = false;
      }
    }
  }

  ngOnInit() {
    this.eventService.subscribeEvents('task-progress', (event: string, data?: TaskProgressModel) => {
      if (event !== 'task-progress' || data?.taskKey !== this.taskKey) {
        return;
      }

      console.log('task-progress', event, data);

      this.taskModel = data ?? {
        taskKey: this.taskKey,
        completed: 0,
        status: 'completed',
        total: 0,
        customerProfileId: null,
      };

      if (this.taskModel.status === 'in-progress') {
        this.isTaskInProgress = true;
        this.progressValue = (this.taskModel.completed / this.taskModel.total) * 100;
      } else {
        this.isTaskInProgress = false;
        this.progressValue = 0;
      }
    });

    this.eventService.sendMessage({ event: 'task-progress', data: { taskKey: this.taskKey } });
  }

  ngOnDestroy() {
    console.log('Unsubscribing from task-progress');
    //this.eventService.unsubscribeEvents('task-progress');
  }
}
